import { z } from "zod";

import { getNormalEnum } from "../pseudoEnums.js";

import {
  AnthropicModelLiteral,
  DeprecatedAnthropicModelLiteral,
} from "./anthropicTypes.js";
import { ChatCompletionsModelLiteral } from "./chatCompletionsTypes.js";
import {
  DeprecatedOpenAiChatModelLiteral,
  OpenAiChatModelLiteral,
} from "./openAiTypes.js";

/**
 * Structural exemptions are always exempt (e.g. BYOK, onboarding,
 * support user) and are never controlled by feature flags.
 */
const StructuralExemptBillingReasonLiteral = z.enum([
  /**
   * The first onboarding project is exempt from billing
   */
  "ONBOARDING_PROJECT",
  /**
   * Orgs with BYOK do not get charged for usage
   */
  "BYOK",
  /*
   * We don't bill directly for online evals, but we do track them for statistics
   * and in case we want to bill for them in the future.
   */
  "ONLINE_EVAL",
  /**
   * We don't bill directly for suggested prompt generation, but we do track them for statistics
   */
  "SUGGESTED_PROMPT_GENERATION",
  /*
   * We may use support users to debug agents in customer projects, and we don't
   * want to bill customers for that usage.
   */
  "SUPPORT_USER",
  /*
   * Don't bill for employee usage on customer orgs, so that users who are invited to a workspace (e.g. sales eng, customer eng) can
   * use the product for debugging and onboarding without incurring costs for customer
   */
  "HEX_EMPLOYEE_ON_CUSTOMER_ORG",
  /*
   * Thread summarization is not billed, but we track for statistics
   */
  "THREAD_SUMMARIZATION",
  /*
   * Confusion detection is not billed, but we track for statistics
   */
  "CONFUSION_DETECTION",
  /*
   * Thread topic discovery is not billed, but we track for statistics
   */
  "THREAD_TOPIC_DISCOVERY",
  /*
   * AI summaries for scheduled run notifications are not billed, but we track them for statistics
   */
  "SCHEDULED_RUN_SUMMARY",
  "TAO_NOTEBOOK_CELL_SUMMARIES",
  "TAO_INSPECT_SUMMARY",
  /*
   * Thread topic classification is not billed, but we track for statistics
   */
  "THREAD_CLASSIFICATION",
  /*
   * Memory extraction is not billed, but we track for statistics
   */
  "MEMORY_EXTRACTION",
  /*
   * Hero analysis of customer threads (retrieval-failure classifier and
   * similar Hero producers) is not billed. Tracked for statistics.
   */
  "HERO_ANALYSIS",
  /*
   * Suggestion source grouping into issues is not billed, but we track for statistics
   */
  "SUGGESTION_SOURCE_GROUPING",
  /* Falling back is expensive since we lose caching, so we exempt threads to avoid annoying our customers */
  "AGENT_PROVIDER_FALLBACK",
  /*
   * Marks rows zeroed out when AI billing enforcement is activated for an org. Existing
   * usage in the current billing cycle is wiped (charges zeroed, reason set) so
   * the org starts enforcement with a clean slate while preserving the underlying
   * audit trail (token counts, model, run IDs).
   */
  "PRE_ENFORCEMENT_RESET",
  /* If the agent run results in an error, exempt the entirety of the run to avoid billing the user for the failed attempt */
  "AGENT_RUN_ERROR",
  /*
   * A platform admin has manually flagged this individual user as exempt from AI
   * credit usage (via the `aiCreditExempt` field on the user). Usage is still
   * tracked but never charged, and the credit-limit admission check is skipped.
   */
  "MANUALLY_EXEMPTED_USER",
  /*
   * Used any time the billing workflow encounters a userless row that is not exempt. We then auto-exempt the row to prevent
   * billing for usage that cannot be attributed to a specific user. Should never happen in practice, but is included
   * as a failsafe.
   */
  "UNATTRIBUTED_USAGE",
]);

/**
 * Feature exemptions are for product workflows that start exempt and eventually
 * get billed via the "ai-billing-feature-exemption" LaunchDarkly flag.
 * Callers always pass through an enum with a target exemption reason,
 * and the flag controls whether we thread it into usage tracking.
 */
export const FeatureExemptBillingReasonLiteral = z.enum([
  "VISUALIZATION_SUBAGENT",
  "DATA_APP_CHART_UPGRADE",
  "DATA_APP_SUBAGENT",
  "REVIEW_AGENT",
  "ARTIFACT_EXTRACTION_AGENT",
  "HERO_ANALYZER_AGENT",
  "HERO_AGGREGATOR_AGENT",
  "KNOWLEDGE_CURATION_AGENT",
  "SUBAGENT_WORK_SUMMARIZATION",
]);
export type FeatureExemptBillingReason = z.infer<
  typeof FeatureExemptBillingReasonLiteral
>;
export const FeatureExemptBillingReason = getNormalEnum(
  FeatureExemptBillingReasonLiteral,
);

export const ExemptBillingReasonLiteral = z.enum([
  ...StructuralExemptBillingReasonLiteral.options,
  ...FeatureExemptBillingReasonLiteral.options,
]);
export type ExemptBillingReason = z.infer<typeof ExemptBillingReasonLiteral>;
export const ExemptBillingReason = getNormalEnum(ExemptBillingReasonLiteral);

type ExemptBillingReasonMetadata = {
  /**
   * Whether this exemption's run is one the customer sees
   * Determines if the runs are returned from the Agent Usage API
   */
  isCustomerVisible: boolean;
};
export const EXEMPT_BILLING_REASON_METADATA: Record<
  ExemptBillingReason,
  ExemptBillingReasonMetadata
> = {
  ONBOARDING_PROJECT: { isCustomerVisible: true },
  BYOK: { isCustomerVisible: true },
  HEX_EMPLOYEE_ON_CUSTOMER_ORG: { isCustomerVisible: true },
  AGENT_PROVIDER_FALLBACK: { isCustomerVisible: true },
  AGENT_RUN_ERROR: { isCustomerVisible: true },
  MANUALLY_EXEMPTED_USER: { isCustomerVisible: true },
  DATA_APP_CHART_UPGRADE: { isCustomerVisible: true },
  VISUALIZATION_SUBAGENT: { isCustomerVisible: true },
  DATA_APP_SUBAGENT: { isCustomerVisible: true },
  PRE_ENFORCEMENT_RESET: { isCustomerVisible: true },
  SUPPORT_USER: { isCustomerVisible: false },
  // Safety-net exemption for unattributed rows; internal, never surfaced.
  UNATTRIBUTED_USAGE: { isCustomerVisible: false },
  SUBAGENT_WORK_SUMMARIZATION: { isCustomerVisible: false },
  ONLINE_EVAL: { isCustomerVisible: false },
  SUGGESTED_PROMPT_GENERATION: { isCustomerVisible: false },
  THREAD_SUMMARIZATION: { isCustomerVisible: false },
  CONFUSION_DETECTION: { isCustomerVisible: false },
  THREAD_TOPIC_DISCOVERY: { isCustomerVisible: false },
  SCHEDULED_RUN_SUMMARY: { isCustomerVisible: false },
  TAO_NOTEBOOK_CELL_SUMMARIES: { isCustomerVisible: false },
  TAO_INSPECT_SUMMARY: { isCustomerVisible: false },
  THREAD_CLASSIFICATION: { isCustomerVisible: false },
  MEMORY_EXTRACTION: { isCustomerVisible: false },
  HERO_ANALYSIS: { isCustomerVisible: false },
  SUGGESTION_SOURCE_GROUPING: { isCustomerVisible: false },
  REVIEW_AGENT: { isCustomerVisible: false },
  ARTIFACT_EXTRACTION_AGENT: { isCustomerVisible: false },
  HERO_ANALYZER_AGENT: { isCustomerVisible: false },
  HERO_AGGREGATOR_AGENT: { isCustomerVisible: false },
  KNOWLEDGE_CURATION_AGENT: { isCustomerVisible: false },
};

/**
 * Exempt reasons on runs the customer sees. The agent usage API returns billed
 * runs plus these (at zero credits); The reason is not exposed but the run is.
 */
export const CUSTOMER_VISIBLE_EXEMPT_BILLING_REASONS: ExemptBillingReason[] =
  Object.entries(EXEMPT_BILLING_REASON_METADATA)
    .filter(([, meta]) => meta.isCustomerVisible)
    .map(([reason]) => reason as ExemptBillingReason);

export const RawBillingQuantityDetails = z.object({
  inputTokens: z.number(),
  outputTokens: z.number(),
  cachedInputTokens: z.number(),
  cacheWrites: z.number(),
  computeUnits: z.number().optional(),
});
export type RawBillingQuantityDetails = z.infer<
  typeof RawBillingQuantityDetails
>;

/**
 * Billing-only identifiers for Anthropic fast mode. Fast mode is the same wire
 * model run with `speed: "fast"`, but Anthropic charges a per-model premium, so
 * we resolve and price it as a distinct {@link BillableModel}. These are
 * intentionally NOT part of `AnthropicModelLiteral`: they are never sent on the
 * wire and have no `AnthropicModelFeatureMap` entry.
 * @see https://platform.claude.com/docs/en/build-with-claude/fast-mode
 */
export const FastAnthropicBillableModelLiteral = z.enum([
  "CLAUDE_4_8_OPUS_FAST",
  "CLAUDE_OPUS_5_FAST",
  "CLAUDE_OPUS_5_5_FAST",
]);
export type FastAnthropicBillableModel = z.infer<
  typeof FastAnthropicBillableModelLiteral
>;
export const FastAnthropicBillableModel = getNormalEnum(
  FastAnthropicBillableModelLiteral,
);

/**
 * Billing-only identifiers for OpenAI Fast mode. Fast mode is the same wire
 * model with a premium service tier.
 */
export const FastOpenAiBillableModelLiteral = z.enum([
  "GPT_56_FAST",
  "GPT_6_ASTRA_FAST",
  "GPT_6_LUNA_FAST",
  "GPT_6_SOL_FAST",
  "GPT_61_SOL_FAST",
]);
export type FastOpenAiBillableModel = z.infer<
  typeof FastOpenAiBillableModelLiteral
>;
export const FastOpenAiBillableModel = getNormalEnum(
  FastOpenAiBillableModelLiteral,
);

// Note that if we want to use the same model from different providers, we can prefix the provider to the model name
/**
 * A billable model is a model we have defined a price for how much we charge customers to use
 */
export const BillableModel = z.union([
  AnthropicModelLiteral,
  FastAnthropicBillableModelLiteral,
  FastOpenAiBillableModelLiteral,
  OpenAiChatModelLiteral,
  ChatCompletionsModelLiteral,
  // We need to keep around deprecated models here for historic
  // billing to be accurate since we store these in the DB
  DeprecatedAnthropicModelLiteral,
  DeprecatedOpenAiChatModelLiteral,
]);
export type BillableModel = z.infer<typeof BillableModel>;
